"use client";

import type { LocationItem } from "~www/components/PageBuilder/blocks/LocationSearchResultSection/types";

import { useQuery } from "@tanstack/react-query";
import { getLocationSearchResults } from "~www/components/PageBuilder/data/locations-search/get-locations-search-results";

type UseCustomerServiceOfficesParams = {
  /** Only fetches when postal code is non-empty and valid */
  postalCode: string;
  forceProximitySearch?: boolean;
};

/**
 * Fetches nearby offices for the Customer Service Form by delegating to the
 * existing `getLocationSearchResults` server action.
 *
 * Calling a `"use server"` function from the client via React Query is
 * intentional. Next.js serializes the RPC automatically, so this reuses the
 * same office-search logic as `LocationSearchResultSection` without a separate
 * API route.
 *
 * The query is disabled when `postalCode` is empty, so it is safe to pass
 * the raw field value directly.
 */
function useCustomerServiceOffices({
  forceProximitySearch = false,
  postalCode,
}: UseCustomerServiceOfficesParams) {
  return useQuery<LocationItem[]>({
    enabled: Boolean(postalCode.trim()),
    queryFn: () => getLocationSearchResults(postalCode, forceProximitySearch),
    queryKey: ["customerServiceOffices", forceProximitySearch, postalCode],
  });
}

export { useCustomerServiceOffices };
